/**
 * Registry central de query keys.
 *
 * Mantener las keys aquí (en vez de strings sueltos) tiene tres ventajas:
 *   · Renombrar es fácil · sin grep de strings mágicos.
 *   · `qc.invalidateQueries({ queryKey: qk.prorrateo(id) })` queda tipado.
 *   · Garantiza unicidad · ningún feature inventa una key que choque con otra.
 *
 * Convención: la primera entrada del array es el "namespace" (solicitud,
 * prorrateo, catalogos…), las siguientes son los discriminadores.
 */
export const qk = {
  solicitud: (id: string) => ["solicitud", id] as const,
  ventanasPuePpd: (id: string) => ["ventanas-pue-ppd", id] as const,
  prorrateo: (id: string) => ["prorrateo", id] as const,
  prorrateos: (id: string) => ["prorrateos", id] as const,
  proveedoresAgrupados: (id: string) => ["proveedores-agrupados", id] as const,
  documentos: (id: string) => ["documentos", id] as const,
  versionesDocumento: (documentoId: string) => ["documentos", "versiones", documentoId] as const,
  requisitosExpediente: (solicitudId: string) => ["documentos", "requisitos", solicitudId] as const,
  documentosSolicitud: (solicitudId: string) => ["documentos", "solicitud", solicitudId] as const,
  polizas: (id: string) => ["polizas", id] as const,
  cecos: ["catalogos", "cecos"] as const,
  // CeCos acotados a la organización de una solicitud (relación CeCo↔organización).
  cecosPorSociedad: (numeroSociedad?: string) =>
    ["catalogos", "cecos", numeroSociedad ?? "todos"] as const,
  cuentasPorSociedad: (numeroSociedad?: string) =>
    ["catalogos", "cuentas", "sociedad", numeroSociedad ?? null] as const,
  cuentas: ["catalogos", "cuentas"] as const,
  clavesSat: ["catalogos", "claves-sat"] as const,
  areas: ["catalogos", "areas"] as const,
  tiposDocumento: ["catalogos", "tipos-documento"] as const,
  indicadoresImpuesto: ["catalogos", "indicadores-impuesto"] as const,
  misSolicitudes: ["mis-solicitudes"] as const,
  seguridad2FA: ["seguridad", "2fa"] as const,
  miPerfil: ["perfil", "mi-perfil"] as const,
  misAsignaciones: ["perfil", "asignaciones"] as const,
  misFacturas: (filtros: object = {}) => ["mis-facturas", filtros] as const,
  /** Facturas (acuse más reciente por UUID) de UNA solicitud · fila expandible de /solicitudes. */
  facturasDeSolicitud: (solicitudId: string) => ["facturas-de-solicitud", solicitudId] as const,
  /** Invoice (factura extranjera · subtipo INVOICE) de UNA solicitud · fila expandible de /solicitudes. */
  invoiceDeSolicitud: (solicitudId: string) => ["invoice-de-solicitud", solicitudId] as const,
  misEstadisticas: ["mis-estadisticas"] as const,
  adminAreas: ["admin", "areas"] as const,
  satDefault: ["admin", "sat-mock", "default"] as const,
  satOverrides: ["admin", "sat-mock", "overrides"] as const,
  bandejaAutorizacion: ["autorizacion", "bandeja"] as const,
  historialAutorizaciones: ["autorizacion", "historial"] as const,
  adminOrganizaciones: ["admin", "organizaciones"] as const,
  adminFlujosAutorizacion: ["admin", "flujos-autorizacion"] as const,
  adminProveedores: ["admin", "proveedores"] as const,
  adminUsuarios: ["admin", "usuarios"] as const,
  adminRoles: ["admin", "roles"] as const,
  modulosDeUsuario: (usuarioId: string) => ["admin", "modulos", "usuario", usuarioId] as const,
  adminPermisos: ["admin", "permisos"] as const,
  adminPermisosGestion: ["admin", "permisos", "gestion"] as const,
  adminCategoriasDocumento: ["admin", "categorias-documento"] as const,
  adminEquivalenciasUnidad: ["admin", "equivalencias-unidad"] as const,
  adminIndicadoresImpuesto: ["admin", "indicadores-impuesto"] as const,
  adminProrrateoAutomatico: ["admin", "prorrateo-automatico"] as const,
  adminClavesSatClavesCuenta: ["admin", "mapeos-clave-cuenta"] as const,
  adminClavesSatCentrosCosto: ["admin", "clave-sat-centro-costo"] as const,
  adminTiposDocumento: ["admin", "tipos-documento"] as const,
  /** Hotspot 55 · eventos del outbox que fallaron al despacharse. */
  adminEventosFallidos: ["admin", "eventos-fallidos"] as const,
  // Módulo de Notificaciones (diseñador + bitácora).
  notifCatalogo: ["admin", "notificaciones", "catalogo"] as const,
  notifFlujos: ["admin", "notificaciones", "flujos"] as const,
  notifFlujo: (id: string) => ["admin", "notificaciones", "flujo", id] as const,
  notifVersiones: (id: string) => ["admin", "notificaciones", "flujo", id, "versiones"] as const,
  notifEnvios: ["admin", "notificaciones", "envios"] as const,
  notifEnvio: (id: string) => ["admin", "notificaciones", "envio", id] as const,
  // Perfil · canales y preferencias de notificación del usuario.
  misCanalesNotif: ["mis-notificaciones", "canales"] as const,
  misPreferenciasNotif: ["mis-notificaciones", "preferencias"] as const,
  adminConfiguraciones: ["admin", "configuraciones"] as const,
  adminOrdenesCompra: ["admin", "ordenes-compra"] as const,
  adminOrdenCompra: (clave: string) => ["admin", "ordenes-compra", clave] as const,
  adminContratosAnualizados: ["admin", "contratos-anualizados"] as const,
  adminContratoAnualizado: (clave: string) => ["admin", "contratos-anualizados", clave] as const,
  auditoriaDocumentos: ["admin", "auditoria-documentos"] as const,
  auditoriaCambios: ["admin", "auditoria-cambios"] as const,
  solicitudCatalogoEstado: (tipo: string, clave: string, numeroSociedad?: string) =>
    ["solicitud-catalogo", "estado", tipo, clave, numeroSociedad ?? null] as const,
  tiposSolicitudCatalogoActivos: ["solicitud-catalogo", "tipos"] as const,
  adminTiposSolicitudCatalogo: ["admin", "tipos-solicitud-catalogo"] as const,
  misSolicitudesCatalogo: ["solicitud-catalogo", "mias"] as const,
  misNotificaciones: ["notificaciones", "mias"] as const,
  misNotificacionesCount: ["notificaciones", "mias", "no-vistas"] as const,
  // Hotspot 111 · avisos del canal Campana (módulo Notificaciones · /api/v1/avisos).
  misAvisos: ["avisos", "mios"] as const,
  misAvisosCount: ["avisos", "mios", "no-vistos"] as const,
  bandejaSolicitudesCatalogo: ["bandeja-solicitudes-catalogo"] as const,
  solicitudesCatalogoPendientes: ["bandeja-solicitudes-catalogo", "pendientes"] as const,
  misTiposResponsableCatalogo: ["bandeja-solicitudes-catalogo", "mis-tipos"] as const,
  // Gestión Documental · fase 3 · revisión documental y expediente de una solicitud.
  bandejaRevisionDocumental: ["revision-documental"] as const,
  revisionDocumentalPendientes: ["revision-documental", "pendientes"] as const,
  expediente: (solicitudId: string) => ["expediente", solicitudId] as const,
  calendarioPagos: (filtros: object = {}) => ["calendario-pagos", filtros] as const,
  calendarioPagosOrganizaciones: ["calendario-pagos-organizaciones"] as const,
  historicoFiltros: ["historico", "filtros"] as const,
  historicoBusqueda: (filtros: object) => ["historico", "busqueda", filtros] as const,
  chat: (id: string) => ["chat", id] as const,
  historialSolicitud: (id: string) => ["historial-solicitud", id] as const,
  // P44 · FB60 Contrapago
  misOrganizaciones: ["catalogos", "organizaciones"] as const,
  proveedoresBuscar: (params: object) => ["proveedores-buscar", params] as const,
  enEsperaCfdi: (params: object) => ["en-espera-cfdi", params] as const,
  // Reportería (builder de usuario · S11)
  repCatalogo: ["rep", "catalogo"] as const,
  repRelaciones: ["rep", "relaciones"] as const,
  repTemplates: ["rep", "templates"] as const,
  repColumnas: ["rep", "columnas-personalizadas"] as const,
  repPredefinidos: ["rep", "predefinidos"] as const,
  repTrabajos: ["rep", "trabajos"] as const,
  repValores: (token: string) => ["rep", "valores", token] as const,
  repTotal: (firma: string) => ["rep", "total", firma] as const,
  repPreview: (firma: string) => ["rep", "preview", firma] as const,
  // Reportería · administración de configuraciones
  repAdminConfigs: ["rep", "admin", "configuraciones"] as const,
  repAdminConfig: (id: number) => ["rep", "admin", "configuracion", id] as const,
  repAdminTablas: ["rep", "admin", "introspeccion", "tablas"] as const,
  repAdminColumnas: (esquema: string, tabla: string) =>
    ["rep", "admin", "introspeccion", "columnas", esquema, tabla] as const,
  repAdminRoles: ["rep", "admin", "roles"] as const,
  repAdminUsuarios: ["rep", "admin", "usuarios"] as const,
  // Reportería · builder de predefinidos en contexto de config
  repAdminDefCatalogo: (idConfig: number) => ["rep", "admin", "def-catalogo", idConfig] as const,
  repAdminDefRelaciones: (idConfig: number) => ["rep", "admin", "def-relaciones", idConfig] as const,
  repAdminPredefinidos: (idConfig: number) => ["rep", "admin", "predefinidos", idConfig] as const,
  repAdminDefPreview: (idConfig: number, firma: string) => ["rep", "admin", "def-preview", idConfig, firma] as const,
  repAdminDefTotal: (idConfig: number, firma: string) => ["rep", "admin", "def-total", idConfig, firma] as const,
} as const;
